import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Tabs,
  Tab,
  Fade,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vaccination-tabpanel-${index}`}
      aria-labelledby={`vaccination-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Vaccination: React.FC = () => {
  
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // const handleFilterChange = (event: any) => {
  //   setFilterType(event.target.value as 'all' | 'national' | 'ideal');
  // };

  // const idealVaccinationSchedule = [
  //   {
  //     age: 'Новорожденные (в первые 24 часа)',
  //     ageMonths: 0,
  //     vaccines: [
  //       { name: 'Гепатит В', mandatory: true, note: 'Первая вакцинация', type: 'national', code: 'HBV-1' }
  //     ]
  //   },
  // ];

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h2" sx={{ mb: 2, color: '#2D3748', fontWeight: 700 }}>
          💉 Календарь вакцинации
        </Typography>
        <Typography variant="body1" sx={{ color: '#718096', maxWidth: 800, fontSize: '1.1rem', mb: 3 }}>
          Национальный календарь профилактических прививок Российской Федерации 
          с учетом рекомендаций ВОЗ и международного опыта. Включает обязательные 
          прививки из национального календаря и дополнительные рекомендуемые вакцины.
        </Typography>

        {/* Информационная карточка о том, как читать календарь */}
        <Card sx={{ 
          mb: 4, 
          background: 'linear-gradient(135deg, #E3F2FD 0%, #F3E5F5 100%)',
          border: '1px solid #BBDEFB',
          borderRadius: 2
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <InfoIcon sx={{ color: '#1976D2', mr: 1.5, fontSize: 24 }} />
              <Typography variant="h6" sx={{ 
                color: '#1976D2', 
                fontWeight: 600,
                fontSize: '1.1rem'
              }}>
                Как читать календарь вакцинации
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ color: '#424242', mb: 1, fontWeight: 500 }}>
                  📅 <strong>Сроки вакцинации:</strong> Конкретные возрасты для каждой прививки
                </Typography>
                <Typography variant="body2" sx={{ color: '#424242', mb: 1, fontWeight: 500 }}>
                  🏷️ <strong>Типы вакцин:</strong> Обязательные, рекомендуемые, по показаниям
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: '#424242', mb: 1, fontWeight: 500 }}>
                  👶 <strong>Возрастные группы:</strong> Для кого предназначена вакцина
                </Typography>
                <Typography variant="body2" sx={{ color: '#424242', mb: 1, fontWeight: 500 }}>
                  💉 <strong>Количество доз:</strong> Сколько раз нужно привиться
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Alert severity="info" sx={{ mb: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(74, 144, 226, 0.15)' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <InfoIcon sx={{ color: '#4A90E2', mr: 1, mt: 0.5 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Источник информации
            </Typography>
            <Typography variant="body2">
              Календарь основан на официальном Национальном календаре профилактических прививок РФ 
              и рекомендациях ВОЗ. Красные точки обозначают обязательные прививки, 
              синие - рекомендуемые дополнительные вакцины.
            </Typography>
          </Box>
        </Box>
      </Alert>

      <Card sx={{ 
        mb: 3, 
        borderRadius: 3, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              sx={{ 
                px: 3, 
                '& .MuiTab-root': { 
                  fontWeight: 600, 
                  textTransform: 'none',
                  fontSize: '1rem'
                }
              }}
            >
              <Tab 
                label="Календарь прививок" 
                icon={<CalendarIcon />} 
                iconPosition="start"
                sx={{ minHeight: 64 }}
              />
              <Tab 
                label="Догоняющая вакцинация" 
                icon={<ScheduleIcon />} 
                iconPosition="start"
                sx={{ minHeight: 64 }}
              />
            </Tabs>
          </Box>
        </CardContent>
      </Card>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box>
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 600, color: '#4A90E2' }}>
              Интерактивная схема вакцинации
            </Typography>
            <Typography variant="body1" sx={{ color: '#718096', mb: 2 }}>
              Наведите курсор на точки для получения подробной информации о каждой прививке
            </Typography>
            
            {/* Поиск и фильтры */}
            </Box>
          </Box>

        </Box>


        {/* Календарь вакцинации в виде схемы */}
        <Card sx={{ 
          borderRadius: 3, 
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
        }}>
          <CardContent sx={{ p: 0 }}>
            {/* Заголовок с возрастными отметками */}

            {/* Строки вакцин */}
            <Box sx={{ 
              maxHeight: { xs: '60vh', md: '70vh' }, 
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: 6,
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: 3,
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: 3,
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a8a8a8',
              }
            }}>
              {[
                { name: 'Гепатит В', type: 'national', points: [0, 1, 6], notes: ['1-я', '2-я', '3-я'], description: 'Защита от вирусного гепатита В' },
                { name: 'Туберкулёз (БЦЖ)', type: 'national', points: [0, 7*12], notes: ['Вакцинация', 'Ревакцинация по показаниям'], description: 'Защита от туберкулеза' },
                { name: 'Пневмококковая', type: 'national', points: [2, 4.5, 15], notes: ['1-я', '2-я', '3-я'], description: 'Защита от пневмококковой инфекции' },
                { name: 'Ротавирусная', type: 'ideal', points: [2, 3, 4.5], notes: ['1-я', '2-я', '3-я'], description: 'Защита от ротавирусного гастроэнтерита' },
                { name: 'АКДС', type: 'national', points: [3, 4.5, 6, 18, 6*12, 14*12], notes: ['1-я', '2-я', '3-я', '4-я', 'Ревакцинация в 6 лет', 'Ревакцинация в 14 лет'], description: 'Защита от дифтерии, коклюша, столбняка' },
                { name: 'Гемофильная b', type: 'national', points: [3, 4.5, 6, 18], notes: ['1-я', '2-я', '3-я', 'Ревакцинация в 18 месяцев'], description: 'Защита от гемофильной инфекции' },
                { name: 'Полиомиелит', type: 'national', points: [3, 4.5, 6, 18, 20, 6*12], notes: ['1-я', '2-я', '3-я', 'Ревакцинация в 18 месяцев', 'Ревакцинация в 20 месяцев', 'Ревакцинация в 6 лет'], description: 'Защита от полиомиелита' },
                { name: 'Менингококковая ACWY', type: 'ideal', points: [3, 6, 12, 11*12], notes: ['1-я', '2-я', '3-я', '4-я'], description: 'Защита от менингококковой инфекции' },
                { name: 'Менингококковая В', type: 'ideal', points: [3, 4.5, 6, 14*12], notes: ['1-я', '2-я', '3-я', '4-я'], description: 'Защита от менингококка группы В' },
                { name: 'Корь, краснуха, паротит', type: 'national', points: [12, 6*12], notes: ['1-я', '2-я'], description: 'Защита от кори, краснухи, паротита' },
                { name: 'Ветряная оспа', type: 'ideal', points: [12, 15], notes: ['1-я', 'Ревакцинация в 15 месяцев'], description: 'Защита от ветряной оспы' },
                { name: 'Гепатит А', type: 'ideal', points: [12, 18], notes: ['1-я', 'Ревакцинация в 18 месяцев'], description: 'Защита от вирусного гепатита А' },
                { name: 'ВПЧ', type: 'ideal', points: [12*12, 13*12], notes: ['1-я', 'Ревакцинация в 13 лет'], description: 'Защита от вируса папилломы человека' },
                { name: 'Грипп', type: 'ideal', points: [6, 60*12], notes: ['Ежегодно'], description: 'Защита от гриппа' },
                { name: 'Клещевой энцефалит', type: 'indication', points: [12, 13, 24], notes: ['1-я', '2-я через месяц', 'Ревакцинация через год'], description: 'Защита от клещевого энцефалита' },
              ].map((vaccine, vaccineIndex) => (
                <Fade key={vaccineIndex} in={true} timeout={300 + vaccineIndex * 100}>
                  <Card sx={{ 
                    mb: 2,
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '1px solid #E9ECEF',
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    }
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        mb: 3,
                        pb: 2,
                        borderBottom: '2px solid #F1F3F4'
                      }}>
                        <Box sx={{ 
                          width: 16, 
                          height: 16, 
                          borderRadius: '50%', 
                          backgroundColor: vaccine.type === 'national' ? '#FF6B6B' : 
                                         vaccine.type === 'ideal' ? '#4A90E2' : '#666',
                          mr: 2,
                          mt: 0.5,
                          flexShrink: 0
                        }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 700, 
                            color: '#2D3748',
                            mb: 0.5,
                            fontSize: '1.25rem'
                          }}>
                            {vaccine.name}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: '#718096',
                            fontSize: '0.875rem'
                          }}>
                            {vaccine.points.length} {vaccine.points.length === 1 ? 'доза' : 
                                                   vaccine.points.length < 5 ? 'дозы' : 'доз'}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          px: 2.5, 
                          py: 1, 
                          borderRadius: 2,
                          backgroundColor: vaccine.type === 'national' ? '#FFEBEE' : 
                                         vaccine.type === 'ideal' ? '#E3F2FD' : '#F5F5F5',
                          border: `2px solid ${vaccine.type === 'national' ? '#FFCDD2' : 
                                                   vaccine.type === 'ideal' ? '#BBDEFB' : '#E0E0E0'}`,
                          flexShrink: 0
                        }}>
                          <Typography variant="body2" sx={{ 
                            color: vaccine.type === 'national' ? '#D32F2F' : 
                                   vaccine.type === 'ideal' ? '#1976D2' : '#666',
                            fontWeight: 700,
                            fontSize: '0.8rem'
                          }}>
                            {vaccine.type === 'national' ? 'ОБЯЗАТЕЛЬНАЯ' : 
                             vaccine.type === 'ideal' ? 'РЕКОМЕНДУЕМАЯ' : 'ПО ПОКАЗАНИЯМ'}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Typography variant="body2" sx={{ 
                        color: '#718096', 
                        mb: 2,
                        lineHeight: 1.5
                      }}>
                        {vaccine.description}
                      </Typography>


                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ 
                          color: '#2D3748', 
                          fontWeight: 600, 
                          mb: 1.5,
                          fontSize: '0.875rem'
                        }}>
                          📅 Сроки вакцинации:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                          {vaccine.points.map((age, ageIndex) => {
                            let ageText = '';
                            let ageDescription = '';
                            
                            if (age < 12) {
                              ageText = `${age} мес.`;
                              ageDescription = age === 0 ? 'при рождении' : 
                                            age === 1 ? '1 месяц' : 
                                            age === 2 ? '2 месяца' : 
                                            age === 3 ? '3 месяца' : 
                                            age === 4.5 ? '4.5 месяца' : 
                                            age === 6 ? '6 месяцев' : 
                                            age === 9 ? '9 месяцев' : 
                                            `${age} месяцев`;
                            } else if (age === 12) {
                              ageText = '1 год';
                              ageDescription = '12 месяцев';
                            } else if (age === 15) {
                              ageText = '15 мес.';
                              ageDescription = '1 год 3 месяца';
                            } else if (age === 18) {
                              ageText = '18 мес.';
                              ageDescription = '1 год 6 месяцев';
                            } else {
                              const years = Math.floor(age / 12);
                              const months = age % 12;
                              ageText = `${years} лет`;
                              ageDescription = months > 0 ? `${years} лет ${months} мес.` : `${years} лет`;
                            }
                            
                            const note = vaccine.notes[ageIndex] || '';
                            
                            return (
                              <Box key={ageIndex} sx={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 0.5,
                                px: 2.5, 
                                py: 1.5, 
                                borderRadius: 2,
                                backgroundColor: vaccine.type === 'national' ? '#FFEBEE' : 
                                               vaccine.type === 'ideal' ? '#E3F2FD' : '#F5F5F5',
                                border: `2px solid ${vaccine.type === 'national' ? '#FFCDD2' : 
                                                       vaccine.type === 'ideal' ? '#BBDEFB' : '#E0E0E0'}`,
                                minWidth: 'fit-content',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ 
                                    width: 10, 
                                    height: 10, 
                                    borderRadius: '50%', 
                                    backgroundColor: vaccine.type === 'national' ? '#FF6B6B' : 
                                                   vaccine.type === 'ideal' ? '#4A90E2' : '#666'
                                  }} />
                                  <Typography variant="body2" sx={{ 
                                    color: vaccine.type === 'national' ? '#D32F2F' : 
                                           vaccine.type === 'ideal' ? '#1976D2' : '#666',
                                    fontWeight: 700,
                                    fontSize: '0.9rem'
                                  }}>
                                    {ageText}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ 
                                  color: '#718096',
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  ml: 1.5
                                }}>
                                  {ageDescription}
                                </Typography>
                                {note && (
                                  <Typography variant="caption" sx={{ 
                                    color: vaccine.type === 'national' ? '#D32F2F' : 
                                           vaccine.type === 'ideal' ? '#1976D2' : '#666',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    ml: 1.5,
                                    mt: 0.5,
                                    px: 1,
                                    py: 0.25,
                                    backgroundColor: 'rgba(255,255,255,0.7)',
                                    borderRadius: 1
                                  }}>
                                    {note}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              ))}
            </Box>
          </CardContent>
        </Card>

      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ScheduleIcon sx={{ color: '#4A90E2', mr: 1, fontSize: 28 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#4A90E2' }}>
              Догоняющая вакцинация по CDC
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ color: '#718096', mb: 3 }}>
            Рекомендации Центра по контролю и профилактике заболеваний США (CDC) 
            по догоняющей вакцинации детей с отставанием в графике прививок.
          </Typography>
        </Box>

        {/* Информация о догоняющей вакцинации */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#4A90E2' }}>
              Принципы догоняющей вакцинации
            </Typography>
            <List dense>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Не повторять дозы, полученные в правильном возрасте"
                  sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Минимальные интервалы между дозами должны соблюдаться"
                  sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Возрастные ограничения для первой дозы должны учитываться"
                  sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Одновременное введение вакцин безопасно и эффективно"
                  sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Догоняющие графики по возрасту */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#4A90E2' }}>
              Догоняющие графики по возрасту
            </Typography>
            
            {/* Дети 4 месяца - 6 лет */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, color: '#FF6B6B', fontSize: '1.1rem' }}>
                Дети 4 месяца - 6 лет
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                Для детей, которые начали вакцинацию в возрасте 4 месяцев и старше
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#4A90E2' }}>
                    DTaP (Дифтерия, коклюш, столбняк)
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary="• 3 дозы с интервалом 4 недели"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary="• 4-я доза через 6 месяцев после 3-й"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary="• 5-я доза в возрасте 4-6 лет"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem' } }}
                      />
                    </ListItem>
                  </List>
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#4A90E2' }}>
                    IPV (Полиомиелит)
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary="• 3 дозы с интервалом 4 недели"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary="• 4-я доза через 6 месяцев после 3-й"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary="• 5-я доза в возрасте 4-6 лет"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem' } }}
                      />
                    </ListItem>
                  </List>
                </Box>
              </Box>
            </Box>

            {/* Дети 7-18 лет */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, color: '#FF6B6B', fontSize: '1.1rem' }}>
                Дети 7-18 лет
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                Для детей школьного возраста с неполной вакцинацией
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#4A90E2' }}>
                    Tdap (Столбняк, дифтерия, коклюш)
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary="• 1 доза Tdap в возрасте 7-18 лет"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary="• Td каждые 10 лет после Tdap"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem' } }}
                      />
                    </ListItem>
                  </List>
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#4A90E2' }}>
                    MMR (Корь, паротит, краснуха)
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary="• 2 дозы с интервалом 4 недели"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary="• Первая доза в возрасте 12+ месяцев"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem' } }}
                      />
                    </ListItem>
                  </List>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Специальные случаи */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#4A90E2' }}>
              Специальные случаи
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#FF9800', fontSize: '1rem' }}>
                  Иммунокомпрометированные дети
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <WarningIcon sx={{ color: '#FF9800', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Живые вакцины противопоказаны"
                      sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <WarningIcon sx={{ color: '#FF9800', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Требуется консультация иммунолога"
                      sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                    />
                  </ListItem>
                </List>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#4CAF50', fontSize: '1rem' }}>
                  Дети с хроническими заболеваниями
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Вакцинация особенно важна"
                      sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Может потребоваться дополнительная доза"
                      sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                    />
                  </ListItem>
                </List>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Источник информации */}
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(74, 144, 226, 0.15)' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <InfoIcon sx={{ color: '#4A90E2', mr: 1, mt: 0.5 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Источник информации
              </Typography>
              <Typography variant="body2">
                Рекомендации основаны на официальных данных 
                <a href="https://www.cdc.gov/vaccines/schedules/hcp/imz/catchup.html" 
                   target="_blank" rel="noopener noreferrer" 
                   style={{ color: '#4A90E2', textDecoration: 'none', fontWeight: 600 }}>
                  {' '}Центра по контролю и профилактике заболеваний США (CDC)
                </a> по догоняющей вакцинации.
              </Typography>
            </Box>
          </Box>
        </Alert>
      </TabPanel>

    </Box>
  );
};

export default Vaccination;
